import { useCallback, useMemo } from 'react';
import { useAccountsWithAccountQuery } from '../../../api/accounts';
import { useBeneficiariesQuery } from '../../../api/beneficiaries';
import { useCardsWithCardQuery } from '../../../api/cards';
import {
  AccountChip,
  AccountListItem,
  Autocomplete,
  BeneficiaryChip,
  CardChip,
  CardListItem,
  ListItem,
  TextField,
  TransactionCategoryChip,
  TransactionCategoryListItem,
  VaultChip,
  VaultListItem,
} from '@razz-team/ui-web';
import { ApiAccount, ApiBeneficiary, ApiCard, ApiVault } from '../../../openapi';
import { BeneficiaryListItem } from '../../ListItem/BeneficiaryListItem';
import { isAccountMatch } from '../../../utils/isAccountMatch';
import { isCardMatch } from '../../../utils/isCardMatch';
import { isBeneficiaryMatch } from '../../../utils/isBeneficiaryMatch';
import { useVaultsQuery } from '../../../api/vaults';
import { isVaultMatch } from '../../../utils/isVaultMatch';
import { useTranslation } from 'react-i18next';
import { isCategoryMatch } from '../../../utils/isCategoryMatch';

type Value = {
  cardId?: string;
  vaultId?: string;
  accountId?: string;
  categories?: string[];
  beneficiaryIds?: string[];
};

type Option =
  | { type: 'account'; account: ApiAccount }
  | { type: 'vault'; vault: ApiVault }
  | { type: 'card'; card: ApiCard }
  | { type: 'beneficiary'; beneficiary: ApiBeneficiary }
  | { type: 'category'; category: string };

const TRANSACTION_CATEGORIES = [
  'CARD_PAYMENT',
  'BOOKS_ART',
  'EDUCATION',
  'ELECTRONICS',
  'ENTERTAINMENT',
  'HEALTH_BEAUTY',
  'RESTAURANTS',
  'SHOPPING',
  'TAXI_TRANSPORT',
  'TRAVEL',
  'ATM',
  'INTERNAL_TRANSFER',
  'SAVINGS',
  'CHARITY',
  'EXTERNAL_TRANSFER',
  'OTHER',
];

export function TransactionSearchAutocomplete({
  value,
  onChange,
}: {
  value: Value;
  onChange: (value: Value) => void;
}) {
  const { t } = useTranslation();
  const accountsQuery = useAccountsWithAccountQuery(value.accountId);
  const cardsQuery = useCardsWithCardQuery(value.cardId);
  const beneficiariesQuery = useBeneficiariesQuery();
  const vaultsQuery = useVaultsQuery();
  const options = useMemo(() => {
    return [
      ...(accountsQuery.data ?? []).map((account): Option => ({ type: 'account', account })),
      ...(vaultsQuery.data ?? []).map((vault): Option => ({ type: 'vault', vault })),
      ...(cardsQuery.data ?? []).map((card): Option => ({ type: 'card', card })),
      ...(beneficiariesQuery.data ?? []).map(
        (beneficiary): Option => ({
          type: 'beneficiary',
          beneficiary,
        }),
      ),
      ...TRANSACTION_CATEGORIES.map((category): Option => ({ type: 'category', category })),
    ];
  }, [accountsQuery.data, vaultsQuery.data, cardsQuery.data, beneficiariesQuery.data]);

  const activeEntitiesText = useMemo(
    () =>
      [
        accountsQuery.isEnabled ? t('accounts') : null,
        vaultsQuery.isEnabled ? t('vaults') : null,
        cardsQuery.isEnabled ? t('cards') : null,
        beneficiariesQuery.isEnabled ? t('beneficiaries') : null,
        t('categories'),
      ]
        .filter(Boolean)
        .join(', '),
    [
      accountsQuery.isEnabled,
      vaultsQuery.isEnabled,
      cardsQuery.isEnabled,
      beneficiariesQuery.isEnabled,
      t,
    ],
  );

  const handleChange = useCallback(
    (_: React.SyntheticEvent, nextOptions: Option[]) => {
      const nextValue: Value = {};
      for (const option of nextOptions) {
        switch (option.type) {
          case 'account':
            nextValue.accountId = option.account.id;
            break;
          case 'card':
            nextValue.cardId = option.card.id;
            break;
          case 'beneficiary':
            nextValue.beneficiaryIds = [
              ...(nextValue.beneficiaryIds ?? []),
              option.beneficiary.beneficiaryId,
            ];
            break;
          case 'vault':
            nextValue.vaultId = option.vault.id;
            break;
          case 'category':
            nextValue.categories = [...(nextValue.categories ?? []), option.category];
            break;
        }
      }
      onChange(nextValue);
    },
    [onChange],
  );

  const autocompleteValue = useMemo(() => {
    const options: Option[] = [];
    if (value.accountId) {
      const account = accountsQuery.data?.find((a) => a.id === value.accountId);
      if (account) {
        options.push({ type: 'account', account });
      }
    }
    if (value.vaultId) {
      const vault = vaultsQuery.data?.find((v) => v.id === value.vaultId);
      if (vault) {
        options.push({ type: 'vault', vault });
      }
    }
    if (value.cardId) {
      const card = cardsQuery.data?.find((c) => c.id === value.cardId);
      if (card) {
        options.push({ type: 'card', card });
      }
    }
    if (value.beneficiaryIds) {
      for (const beneficiaryId of value.beneficiaryIds) {
        const beneficiary = beneficiariesQuery.data?.find((b) => b.beneficiaryId === beneficiaryId);
        if (beneficiary) {
          options.push({ type: 'beneficiary', beneficiary });
        }
      }
    }
    if (value.categories) {
      for (const category of value.categories) {
        options.push({ type: 'category', category });
      }
    }
    return options;
  }, [value, accountsQuery.data, vaultsQuery.data, cardsQuery.data, beneficiariesQuery.data]);

  return (
    <Autocomplete
      multiple
      fullWidth
      value={autocompleteValue}
      options={options}
      onChange={handleChange}
      loading={
        accountsQuery.isPending ||
        vaultsQuery.isPending ||
        cardsQuery.isPending ||
        beneficiariesQuery.isPending
      }
      renderInput={(params) => (
        <TextField
          label={t('Search')}
          helperText={t('Filter transactions by {{activeEntities}} or leave empty to include all', {
            activeEntities: activeEntitiesText,
          })}
          slotProps={{
            input: {
              ...params.InputProps,
              // startAdornment: params.InputProps.startAdornment ?? (
              //   <InputAdornment position="start">
              //     <IconButton>
              //       <CalendarMonthIcon />
              //     </IconButton>
              //   </InputAdornment>
              // ),
            },
          }}
          {...params}
        />
      )}
      getOptionKey={(option) => {
        switch (option.type) {
          case 'account':
            return option.account.id;
          case 'vault':
            return option.vault.id;
          case 'card':
            return option.card.id;
          case 'beneficiary':
            return option.beneficiary.beneficiaryId;
          case 'category':
            return option.category;
        }
      }}
      getOptionLabel={(option) => {
        switch (option.type) {
          case 'account':
            return option.account.name ?? option.account.balance.currency;
          case 'vault':
            return option.vault.balance.currency;
          case 'card':
            return option.card.name;
          case 'beneficiary':
            return option.beneficiary.accountNumber.value;
          case 'category':
            return option.category;
        }
      }}
      filterOptions={(options, state) => {
        const inputValue = state.inputValue.toLowerCase().trim();
        return options.filter((option) => {
          switch (option.type) {
            case 'account':
              return isAccountMatch(inputValue, option.account);
            case 'vault':
              return isVaultMatch(inputValue, option.vault);
            case 'card':
              return isCardMatch(inputValue, option.card);
            case 'beneficiary':
              return isBeneficiaryMatch(inputValue, option.beneficiary);
            case 'category':
              return isCategoryMatch(inputValue, option.category);
            default:
              return false;
          }
        });
      }}
      renderOption={(props, option) => {
        switch (option.type) {
          case 'account':
            return (
              <AccountListItem
                {...props}
                key={option.account.id}
                account={option.account}
                withBadge
              />
            );
          case 'vault':
            return <VaultListItem {...props} key={option.vault.id} vault={option.vault} />;
          case 'card':
            return <CardListItem {...props} key={option.card.id} card={option.card} />;
          case 'beneficiary':
            return (
              <BeneficiaryListItem
                {...props}
                key={option.beneficiary.beneficiaryId}
                beneficiary={option.beneficiary}
              />
            );
          case 'category':
            return (
              <TransactionCategoryListItem
                {...props}
                key={option.category}
                category={option.category}
              />
            );
          default:
            return <ListItem {...props} key={props.key} />;
        }
      }}
      renderValue={(values, getItemProps) =>
        values.map((value, index) => {
          switch (value.type) {
            case 'account':
              return (
                <AccountChip
                  {...getItemProps({ index })}
                  key={value.account.id}
                  account={value.account}
                  color="primary"
                />
              );
            case 'vault':
              return (
                <VaultChip
                  {...getItemProps({ index })}
                  key={value.vault.id}
                  vault={value.vault}
                  color="primary"
                />
              );
            case 'card':
              return (
                <CardChip
                  {...getItemProps({ index })}
                  key={value.card.id}
                  card={value.card}
                  color="primary"
                />
              );
            case 'beneficiary':
              return (
                <BeneficiaryChip
                  {...getItemProps({ index })}
                  key={value.beneficiary.beneficiaryId}
                  beneficiary={value.beneficiary}
                  color="primary"
                />
              );
            case 'category':
              return (
                <TransactionCategoryChip
                  {...getItemProps({ index })}
                  key={value.category}
                  category={value.category}
                />
              );
          }
        })
      }
    />
  );
}
